import type { ChapterContent, FirstGameStep } from '../../types';

/**
 * Content Rewrite 3.0 — Diuna: Imperium + Nieśmiertelność.
 * Source: docs/perfect-quality/02_NOWE_GRY_PELNE_INSTRUKCJE/12_DIUNA_IMPERIUM_NIESMIERTELNOSC_PELNA_INSTRUKCJA.md
 * No invented Leader / Imperium / Intrigue / Tleilaxu / Conflict catalogues.
 */
export const diunaImperiumChapters: ChapterContent[] = [
  {
    title: 'Cel gry',
    content: [
      {
        type: 'warning',
        text: 'Status treści: PARTIAL. Ta instrukcja uczy procedur systemu gry podstawowej i dodatku Nieśmiertelność. Teksty konkretnych kart Liderów, Imperium, Intryg, Tleilaxu oraz dokładne nagrody Konfliktów odczytuj z komponentów albo z osobnego, zweryfikowanego katalogu — nie zgaduj ich tutaj.',
      },
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'W tej części poznasz, o co toczy się partia i kiedy gra się kończy. Bez tego łatwo grać „od akcji do akcji” i przegapić moment, w którym trzeba już przygotować Intrygi końca gry oraz ostateczne punkty.',
      },
      {
        type: 'heading',
        text: 'Co to jest?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Diuna: Imperium to gra o budowaniu pozycji na Arrakis. Zdobywasz punkty zwycięstwa przez konflikty, sojusze z frakcjami, karty oraz inne efekty. Nie wygrywasz samym zbieraniem zasobów — zasoby służą do wejścia na pola, wzmacniania talii i udziału w Konflikcie.',
      },
      {
        type: 'heading',
        text: 'Po co istnieje warunek końca?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Koniec gry zmusza graczy do balansu między długoterminowym budowaniem talii a bieżącym wyścigiem o punkty. Gdy ktoś zbliża się do progu, pozostałe decyzje o Konfliktach, Sojuszach i Intrygach nabierają innego ciężaru.',
      },
      {
        type: 'heading',
        text: 'Kiedy kończy się gra?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Koniec gry zostaje uruchomiony po zakończeniu rundy, gdy spełniony jest którykolwiek z tych warunków:',
      },
      {
        type: 'step-list',
        steps: [
          'dowolny gracz ma co najmniej 10 punktów zwycięstwa; albo',
          'wyczerpie się talia Konfliktów.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Po kartach Intrygi końca gry i pozostałych efektach wygrywa gracz z największą liczbą punktów. Oficjalne kryteria remisu stosujesz dopiero po tym rozliczeniu — szczegóły w rozdziale „Koniec gry”.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Anna kończy rundę z 10 punktami na torze. Marek ma 8, Basia 7. Runda jeszcze nie jest „zamknięta”: najpierw kończycie bieżącą fazę Powrotu i sprawdzacie warunek końca. Ponieważ Anna osiągnęła 10 VP, gra wchodzi w procedurę końca: gracze mogą zagrać Intrygi końca gry, rozpatrują efekty kart i Liderów, a dopiero potem porównują wynik. Anna nie wygrywa automatycznie w chwili wejścia na 10 — wygrywa ten, kto po wszystkich efektach ma najwięcej punktów.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: ogłaszać zwycięzcę w chwili zdobycia 10. VP. Najpierw dokończ rundę i rozlicz Intrygi oraz efekty końca gry.',
      },
      {
        type: 'tip',
        text: 'Powiązanie: próg 10 VP i wyczerpanie Konfliktów wracają w „Strukturze rundy” oraz „Końcu gry”. Same sposoby zdobywania punktów opisują „Faza Walki”, „Frakcje i wpływ” oraz efekty kart.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Cel: najwięcej punktów po sygnale końca. Sygnał: 10 VP u dowolnego gracza albo brak dalszych Konfliktów — zawsze po zakończeniu rundy.',
      },
      {
        type: 'heading',
        text: 'Mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'P: Czy gra kończy się natychmiast po zdobyciu 10. punktu? O: Nie — po zakończeniu tej rundy. P: Czy wyczerpanie Konfliktów kończy grę w środku tury? O: Nie; warunek sprawdzasz po zakończeniu rundy.',
      },
    ],
  },
  {
    title: 'Główne mechanizmy',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Tu zbierasz mapę mentalną całej gry: co robisz w turze, skąd biorą się punkty i jak łączą się talia, plansza oraz Konflikt. Kolejne rozdziały rozwijają każdy system osobno.',
      },
      {
        type: 'heading',
        text: 'Co łączy Diunę: Imperium?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Gra łączy kilka systemów, które działają równolegle w każdej rundzie:',
      },
      {
        type: 'step-list',
        steps: [
          'budowanie talii;',
          'wysyłanie Agentów na pola planszy;',
          'zarządzanie wodą, solari, przyprawą i oddziałami;',
          'tory wpływu czterech frakcji;',
          'konflikt rozstrzygany na końcu rundy;',
          'karty Intrygi.',
        ],
      },
      {
        type: 'heading',
        text: 'Po co te systemy istnieją razem?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Agent na planszy daje natychmiastowy efekt pola i karty. Odkrycie buduje talię i siłę Konfliktu. Frakcje dają stałe punkty i Sojusze. Intrygi pozwalają zaskoczyć w kluczowym oknie. Żaden z tych torów nie wystarcza sam — wygrana zwykle wymaga ich połączenia.',
      },
      {
        type: 'heading',
        text: 'Przykład intuicji',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Marek wysyła Agentów na pola frakcji i pustyni, zbiera oddziały do garnizonu, a dopiero w Odkryciu dokłada Miecze. W Fazie Walki jego siła to oddziały już rozmieszczone w Konflikcie plus Miecze — nie „wszystko, co ma w zapasie”. Jednocześnie Basia buduje Perswazję pod zakup mocnej karty Imperium. Obie strategie są legalne; różnią się momentem, w którym zamieniają decyzję na punkty.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: traktować Odkrycie jak „odpoczynek” albo mylić zasoby (woda, solari, przyprawa) z Perswazją służącą do zakupu kart.',
      },
      {
        type: 'tip',
        text: 'Dalej: „Struktura rundy” pokazuje kolejność faz; „Tura Agenta” i „Tura Odkrycia” uczą dwóch trybów tury.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Plansza + talia + Konflikt + frakcje + Intrygi. P: Czy muszę używać wszystkich systemów w każdej rundzie? O: Nie, ale ignorowanie Konfliktu albo frakcji zwykle oddaje punkty rywalom.',
      },
    ],
  },
  {
    title: 'Przygotowanie',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Przygotowanie ustawia planszę, talie, zasoby i pozycje startowe. Błąd tutaj psuje pierwszą rundę: złe Konflikty, brakujący rząd Imperium albo pominięty Ród Hagal w grze na dwóch.',
      },
      {
        type: 'heading',
        text: 'Jak przebiega przygotowanie?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Wykonaj kroki w tej kolejności. Każdy krok ma sens dopiero, gdy poprzedni jest gotowy — najpierw wspólny stół, potem gracze.',
      },
      {
        type: 'step-list',
        steps: [
          'Rozłóż planszę.',
          'Przygotuj cztery tory frakcji i znaczniki Sojuszu.',
          'Zbuduj talię Konfliktów odpowiednią do etapów gry.',
          'Przygotuj talię Imperium i odkryj rząd kart.',
          'Przygotuj talie Intrygi.',
          'Umieść zasoby i oddziały w pulach.',
          'Każdy wybiera Lidera, bierze Agents, znacznik punktacji, kostki i talię startową.',
          'Umieść pozycje startowe wpływu i zasobów.',
          'Ustal pierwszego gracza.',
          'W grze dwuosobowej lub solo przygotuj Ród Hagal zgodnie z osobnym trybem.',
        ],
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Grają Anna, Marek i Basia. Po rozłożeniu planszy ustawiają cztery tory frakcji ze znacznikami Sojuszu, budują talię Konfliktów etapami i odkrywają rząd kart Imperium oraz przygotowują Intrygi. Każda osoba wybiera Lidera, bierze Agentów, znacznik punktacji, kostki i talię startową, po czym ustawia wpływ oraz zasoby startowe. Anna wygrywa ustalenie pierwszego gracza. Ponieważ jest trzech graczy, Ród Hagal nie wchodzi do przygotowania.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: pomijać Ród Hagal w grze dwuosobowej lub solo albo mieszać etapy talii Konfliktów tak, że wczesne nagrody pojawiają się za późno.',
      },
      {
        type: 'tip',
        text: 'Wyjątek / blocker: dokładne wartości startowe Liderów, skład talii startowej i karty usuwane przy dodatku Nieśmiertelność wymagają komponentów lub oficjalnego setupu — oznaczone jako DO WERYFIKACJI w rozdziale dodatku. First Game zaleca 3–4 osoby bez dodatku.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Najpierw stół wspólny, potem Liderzy i start, na końcu inicjatywa; Hagal tylko w 1–2 osobach. P: Czy zawsze przygotowuję Hagal? O: Tylko w grze dwuosobowej lub solo.',
      },
    ],
  },
  {
    title: 'Anatomia karty',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Każda karta Imperium to narzędzie decyzji: albo wysyłasz nią Agenta, albo zostawiasz ją na Odkrycie. Ta część uczy, co czytasz na karcie i dlaczego nie wolno użyć jej w obu trybach w jednej rundzie.',
      },
      {
        type: 'heading',
        text: 'Co może zawierać karta?',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'ikony Agenta po lewej;',
          'górny efekt używany w turze Agenta;',
          'dolną część Odkrycia;',
          'Perswazję;',
          'Miecze;',
          'frakcję/kategorię;',
          'koszt zakupu;',
          'efekty specjalne.',
        ],
      },
      {
        type: 'heading',
        text: 'Kiedy używasz której części?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'W turze Agenta wybierasz kartę z ręki, używasz jej ikon do wejścia na pole i rozpatrujesz górny efekt Agenta. W turze Odkrycia odkrywasz pozostałe karty z ręki i korzystasz z dolnych części: Perswazji, Mieczy oraz efektów Odkrycia. W jednej rundzie karta jest używana albo w turze Agenta, albo później w turze Odkrycia — nie w obu.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Basia ma kartę z ikoną Bene Gesserit, efektem Agenta „zyskaj wpływ” oraz dolną Perswazją i Mieczami. Wysyła nią Agenta na pole frakcji — karta schodzi z ręki jako zagranie Agenta. W Odkryciu tej samej rundy Basia nie może już doliczyć Perswazji ani Mieczy z tej karty, bo została zużyta wcześniej. Perswazję i Miecze policzy tylko z kart, które nadal ma na ręce w momencie Odkrycia.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: używanie tej samej karty jako Agenta i w Odkryciu w jednej rundzie.',
      },
      {
        type: 'tip',
        text: 'Powiązanie: pełna procedura wejścia jest w „Turze Agenta”; zakup za Perswazję w „Turze Odkrycia”. Szczepienie (dodatek) łączy dwie karty w jedną zagrywkę — nadal nie daje automatycznie dwóch Agentów.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Góra = Agent, dół = Odkrycie; jedna karta, jeden tryb na rundę. P: Czy mogę zagrać kartę Agentem, a jej Miecze doliczyć później? O: Nie w tej samej rundzie.',
      },
    ],
  },
  {
    title: 'Struktura rundy',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Runda to szkielet całej partii. Jeśli znasz kolejność faz, wiesz, kiedy wolno wysyłać Agentów, kiedy kupować karty i kiedy liczyć Walkę.',
      },
      {
        type: 'heading',
        text: 'Kolejność faz',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Początek rundy.',
          'Tury graczy.',
          'Walka.',
          'Wytwórcy.',
          'Powrót.',
        ],
      },
      {
        type: 'diagram',
        diagramId: 'procedure-flow',
        alt: 'Kolejność faz rundy Diuny: Imperium',
        fallbackText: 'Początek rundy (nowy Konflikt) → tury Agenta lub Odkrycia → Walka → Wytwórcy → Powrót i sprawdzenie końca gry.',
      },
      {
        type: 'heading',
        text: 'Początek rundy',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Odkryj nową kartę Konfliktu i wykonaj efekty początku rundy. Od tej chwili wszyscy wiedzą, o jakie nagrody toczy się bieżący Konflikt.',
      },
      {
        type: 'heading',
        text: 'Tury graczy',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Gracze naprzemiennie wykonują turę Agenta albo, gdy nie chcą lub nie mogą wysłać kolejnego Agenta, turę Odkrycia. Po wykonaniu tury Odkrycia gracz nie wykonuje już tur Agenta w tej rundzie. Odkrycie możesz wybrać nawet wtedy, gdy masz jeszcze Agenta — to decyzja, nie tylko brak pionków.',
      },
      {
        type: 'heading',
        text: 'Przykład przebiegu',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Na początku rundy odkrywacie Konflikt. Anna wysyła Agenta, Marek wysyła Agenta, Basia wysyła Agenta. W kolejnym okrążeniu Anna znów wysyła Agenta, a Marek — mając jeszcze Agenta w zapasie — decyduje się na Odkrycie, bo chce kupić kartę i zebrać Miecze. Od tej chwili Marek nie wysyła już Agentów do końca rundy. Gdy wszyscy zakończą swoje ścieżki, następuje Walka, potem Wytwórcy i Powrót.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: po Odkryciu próbować wysłać kolejnego Agenta w tej samej rundzie albo rozgrywać Walkę, zanim wszyscy zakończą tury.',
      },
      {
        type: 'tip',
        text: 'Szczegóły faz: „Tura Agenta”, „Tura Odkrycia”, „Faza Walki”, „Wytwórcy”, „Powrót”.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Konflikt → tury → Walka → Wytwórcy → Powrót. P: Czy mogę zrobić Odkrycie, mając jeszcze Agenta? O: Tak; potem nie wysyłasz już Agentów w tej rundzie.',
      },
    ],
  },
  {
    title: 'Tura Agenta',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Tura Agenta to podstawowy sposób interakcji z planszą: wybierasz kartę, wchodzisz na pole, płacisz koszt i rozpatruje efekty. To tu zdobywasz zasoby, wpływ, oddziały i pozycję do Konfliktu.',
      },
      {
        type: 'heading',
        text: 'Co to jest?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Tura Agenta oznacza wysłanie jednego Agenta na wybrane, legalne pole planszy przy pomocy karty z ręki. Karta dostarcza ikonę dostępu i górny efekt; pole dostarcza własny efekt oraz ewentualny koszt wejścia.',
      },
      {
        type: 'heading',
        text: 'Jak przebiega?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Wykonaj kroki po kolei. Nie pomijaj sprawdzenia ikony ani kosztu — bez nich wejście jest nielegalne nawet wtedy, gdy „bardzo chcesz” efekt pola.',
      },
      {
        type: 'step-list',
        steps: [
          'Wybierz kartę z ręki — to karta, którą zużywasz w tej turze jako Agent.',
          'Sprawdź ikonę Agenta i wybierz pasujące, dostępne pole planszy — ikona musi pasować do pola.',
          'Opłać koszt wejścia — tylko jeśli pole lub efekt go wymaga; nie wydaj więcej, niż posiadasz.',
          'Umieść Agenta na polu — pole staje się zwykle zajęte dla kolejnych Agentów.',
          'Rozpatrz efekt pola — natychmiast według tekstu pola.',
          'Rozpatrz efekt Agenta z karty — górna część karty.',
          'Wykonaj efekty dodatkowe w dozwolonej kolejności — gdy karta, pole lub Intryga Fabuły na to pozwala.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Karta bez właściwej ikony nie pozwala wejść na pole, nawet jeśli potrafisz opłacić koszt. Na polu zajętym przez innego Agenta zwykle nie można postawić kolejnego, chyba że reguła pola lub efekt mówi inaczej.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Anna wybiera z ręki kartę z ikoną Bene Gesserit. Na planszy jest wolne pole frakcji Bene Gesserit. Anna sprawdza koszt wejścia, opłaca go, stawia Agenta, otrzymuje wpływ z pola, a następnie rozpatruje górny efekt karty. W następnej własnej turze Agenta wchodzi na wolne pole z ikoną Konfliktu i rozmieszcza dozwoloną liczbę oddziałów z garnizonu do Konfliktu — zgodnie z regułą tego pola.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: wchodzić na pole bez pasującej ikony „bo mam zasoby”; stawiać Agenta na zajętym polu bez wyjątku; rozpatrywać efekt karty przed efektem pola, gdy procedura każe inaczej.',
      },
      {
        type: 'tip',
        text: 'Wyjątek: niektóre pola lub efekty pozwalają wejść mimo zajęcia — tylko wtedy, gdy tekst to mówi. Powiązanie: rozmieszczanie oddziałów opisuje „Oddziały, garnizon i Konflikt”; Intrygi Fabuły mogą mieć własne okno w turze.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Karta + ikona + wolne pole + koszt + efekt pola + efekt karty. P: Czy sam koszt bez ikony wystarczy? O: Nie. P: Czy zajęte pole zawsze blokuje? O: Zwykle tak, chyba że reguła mówi inaczej.',
      },
    ],
  },
  {
    title: 'Pola planszy',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Plansza to mapa legalnych wejść Agenta. Ta część uczy, jak czytać typy pól i ograniczenia zajętości — bez katalogu konkretnych kosztów ani efektów z polskiego wydania, które odczytujesz z komponentu.',
      },
      {
        type: 'heading',
        text: 'Co to jest pole?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Pole to miejsce na planszy, na które możesz wysłać Agenta, gdy masz pasującą ikonę na karcie, możesz opłacić ewentualny koszt wejścia i pole jest dostępne. Każde pole ma własny efekt; niektóre mają ikonę Konfliktu, koszt w zasobach albo powiązanie z frakcją.',
      },
      {
        type: 'heading',
        text: 'Po co istnieją różne typy pól?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Różne strefy planszy służą różnym celom rundy: pola frakcji budują wpływ i Sojusze, pustynia daje przyprawę i rzadkie wejścia za wodę, pola z Konfliktem pozwalają rozmieścić oddziały, a stałe cele w rodzaju Rady czy Mistrza Miecza zmieniają ekonomię kolejnych tur.',
      },
      {
        type: 'heading',
        text: 'Jak czytać pole przed wejściem?',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Sprawdź wymaganą ikonę Agenta — bez niej wejście jest nielegalne mimo posiadania zasobów.',
          'Sprawdź, czy pole jest wolne — zajęte pole zwykle blokuje kolejnego Agenta, chyba że tekst pola lub efekt mówi inaczej.',
          'Odczytaj koszt wejścia z planszy (woda, solari, przyprawa lub inny warunek) i opłać go tylko z posiadanych zasobów.',
          'Po umieszczeniu Agenta rozpatrz efekt pola, potem górny efekt karty, potem dozwolone efekty dodatkowe.',
        ],
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Anna ma kartę z ikoną pustyni i jedną wodę. Na planszy widzi wolne pole pustyni z kosztem wody (odczytanym z planszy). Opłaca wodę, stawia Agenta, bierze efekt pola — na przykład przyprawę — i dopiero potem górny efekt karty. Marek chce wejść na to samo pole w tej samej rundzie: jest już zajęte, więc bez wyjątku z tekstu nie może tam stanąć. Osobno Basia wchodzi na wolne pole z ikoną Konfliktu i rozmieszcza oddziały z garnizonu zgodnie z regułą tego pola.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: zgadywać koszt lub efekt pola „z pamięci innej edycji”; wchodzić bez ikony; stawiać Agenta na zajętym polu bez wyjątku; mylić efekt pola z dolną częścią Odkrycia karty.',
      },
      {
        type: 'tip',
        text: 'Blocker: dokładne koszty, ikony i teksty pól — z planszy polskiego wydania; nie uzupełniono tu liczb ani katalogu pól. Powiązanie: procedura wejścia w „Turze Agenta”; frakcje w „Frakcje i wpływ”; Rada/Mistrz w „Rada i Mistrz Miecza”; rozmieszczanie w „Oddziały, garnizon i Konflikt”.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Ikona + dostępność + koszt z planszy + efekt pola. Katalog wartości pól należy do komponentu, nie do domysłu.',
      },
      {
        type: 'heading',
        text: 'Mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'P: Czy wolno wejść na pole bez pasującej ikony, jeśli mam zasoby? O: Nie. P: Skąd wziąć dokładny koszt pola? O: Z nadruku na planszy polskiego wydania. P: Czy zajęte pole zawsze blokuje? O: Zwykle tak, chyba że reguła mówi inaczej.',
      },
    ],
  },
  {
    title: 'Tura Odkrycia',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Odkrycie zamienia pozostałe karty z ręki w Perswazję, Miecze i efekty dolne. To moment zakupów do talii i budowania siły bieżącego Konfliktu — oraz moment, po którym kończysz wysyłanie Agentów w tej rundzie.',
      },
      {
        type: 'heading',
        text: 'Kiedy wykonujesz Odkrycie?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Gdy nie chcesz lub nie możesz wysłać kolejnego Agenta — albo świadomie wybierasz Odkrycie mimo posiadania Agenta. Po tej turze nie wykonujesz już tur Agenta do końca rundy.',
      },
      {
        type: 'heading',
        text: 'Jak przebiega?',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Odkryj wszystkie pozostałe karty z ręki.',
          'Zsumuj Perswazję.',
          'Zsumuj Miecze.',
          'Rozpatrz efekty Odkrycia.',
          'Kup dowolną liczbę kart, opłacając każdą osobno Perswazją.',
          'Niewydana Perswazja przepada.',
          'Miecze zwiększają siłę w bieżącym Konflikcie.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Kupione karty trafiają zwykle na stos kart odrzuconych, nie do ręki. Nie musisz kupować żadnej karty. Perswazja nie jest zasobem przechowywanym między rundami.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Marek odkrywa trzy karty: łącznie 7 Perswazji i 2 Miecze oraz jeden efekt Odkrycia dający solari. Rozpatruje efekt, kupuje z rzędu Imperium kartę o koszcie 5 za Perswazję i odkłada ją na stos odrzuconych. Pozostałe 2 Perswazji przepadają. 2 Miecze zapisuje jako siłę do nadchodzącej Walki. Nie przenosi zakupionej karty na rękę.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: kupowanie kart za wodę, solari lub przyprawę zamiast za Perswazję; trzymanie niewydanej Perswazji „na następną rundę”; przenoszenie nowej karty prosto do ręki.',
      },
      {
        type: 'tip',
        text: 'Miecze nie są zasobem — to siła bieżącego Konfliktu (patrz „Faza Walki”). Uzupełnianie rzędu Imperium i cykl ręki opisuje „Talia i ręka”.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Perswazja kupuje, Miecze walczą, reszta Perswazji znika. P: Czy muszę kupić kartę? O: Nie. P: Czy niewydana Perswazja przechodzi? O: Nie.',
      },
    ],
  },
  {
    title: 'Talia i ręka',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Talia, ręka, stos odrzuconych i rząd Imperium to cztery różne strefy kart. Ta część uczy, skąd bierzesz karty do ręki, kiedy je zużywasz oraz czym różni się odrzucenie od usunięcia.',
      },
      {
        type: 'heading',
        text: 'Co to jest ręka?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Ręka to karty, które trzymasz prywatnie i z których wybierasz zagrywki w turze Agenta albo które odkrywasz w turze Odkrycia. Karta zagrana Agentem schodzi z ręki i nie weźmie udziału w Odkryciu tej samej rundy. Karty pozostałe na ręce w momencie Odkrycia dostarczają Perswazję, Miecze i dolne efekty.',
      },
      {
        type: 'heading',
        text: 'Jak działa cykl talii?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Gdy talia dobierania jest pusta, a trzeba dobrać kartę, przetasuj stos odrzuconych i utwórz nową talię. Odrzucenie oznacza przeniesienie na stos odrzuconych. Usunięcie karty oznacza trwałe odłożenie jej poza talię — taka karta nie wraca przy tasowaniu.',
      },
      {
        type: 'paragraph',
        text: 'Rząd Imperium uzupełnia się zgodnie z zasadami po zakupach. Kupione karty zwykle trafiają na stos odrzuconych, więc zobaczysz je na ręce dopiero po przetasowaniu i dobraniu — chyba że konkretny efekt mówi inaczej.',
      },
      {
        type: 'heading',
        text: 'Perswazja i zakup kart',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Perswazja powstaje w Odkryciu z dolnych części kart na ręce (oraz bonusów takich jak Radny). Każdą kupowaną kartę opłacasz osobno Perswazją. Niewydana Perswazja przepada. Zakup nie zużywa wody, solari ani przyprawy — te zasoby służą polom i innym efektom, nie rynkowi Imperium.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Basia zaczyna rundę z pięcioma kartami na ręce. Dwiema wysyła Agentów — schodzą z ręki. Trzema pozostałymi robi Odkrycie, kupuje kartę za Perswazję i odkłada zakup na stos odrzuconych. Później ma pustą talię dobierania i musi dobrać: tasuje odrzucone (w tym ten zakup) i tworzy nową talię. Osobno efekt każe usunąć słabą kartę startową — odkłada ją poza grę; ta karta nie wróci przy tasowaniu.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: mylić usunięcie z odrzuceniem; oczekiwać, że zakupiona karta od razu wróci na rękę; płacić za karty Imperium zasobami zamiast Perswazją.',
      },
      {
        type: 'tip',
        text: 'Powiązanie: pełna procedura zakupu jest w „Turze Odkrycia”; anatomia góra/dół karty w „Anatomii karty”; karty Tleilaxu (dodatek) mają osobny rząd zakupu.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Ręka → Agent albo Odkrycie; zakup zwykle na odrzucone; pusta talia → tasowanie; usunięcie = poza grę. P: Czy zakup idzie na rękę? O: Zwykle na stos odrzuconych. P: Czy Perswazja zostaje między rundami? O: Nie.',
      },
    ],
  },
  {
    title: 'Zasoby',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Woda, solari i przyprawa to jawne zasoby stołu. Uczysz się, do czego służą i że nie wolno wydawać więcej, niż posiadasz — oraz że nie zastępują Perswazji.',
      },
      {
        type: 'heading',
        text: 'Woda',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Woda jest rzadkim kosztem wejścia na pustynię i niektórych efektów. Traktuj ją jak ograniczony klucz do silnych pól, nie jak uniwersalną walutę.',
      },
      {
        type: 'heading',
        text: 'Solari',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Solari to waluta używana między innymi do pozyskiwania Mistrza Miecza i Radnego Wysokiej Rady. Dokładne koszty i pola odczytuj z planszy polskiego wydania — rozdział „Rada i Mistrz Miecza”.',
      },
      {
        type: 'heading',
        text: 'Przyprawa',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Przyprawa to wartościowy zasób zdobywany głównie na pustyni i wydawany na silne pola. Bonusowa przyprawa może kumulować się na nieodwiedzonych polach — patrz „Wytwórcy”.',
      },
      {
        type: 'paragraph',
        text: 'Zasoby są jawne i nie można wydać więcej, niż się posiada. Brak legalnej płatności oznacza, że nie wykonujesz kosztownej części akcji.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Anna ma 1 wodę i chce wejść na pole pustyni wymagające wody. Płaci jedyną wodę, wchodzi i bierze efekt. Nie może w tej samej chwili „dołożyć” wody, której nie ma. Osobno Marek w Odkryciu ma 6 Perswazji — kupuje kartę za Perswazję, nie za solari leżące przy jego zasobach.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: płacić za karty Imperium zasobami zamiast Perswazją albo wydawać zasoby „na kredyt”.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Woda / solari / przyprawa są jawne i ograniczone; Perswazja to osobny system zakupu. P: Czy mogę wydać zasób, którego nie mam? O: Nie.',
      },
    ],
  },
  {
    title: 'Oddziały, garnizon i Konflikt',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Oddziały zmieniają znaczenie w zależności od strefy. Ta część uczy różnicy między zapasem, garnizonem i Konfliktem — bez niej Faza Walki liczy się źle.',
      },
      {
        type: 'heading',
        text: 'Gdzie mogą leżeć oddziały?',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'w zasobach gracza;',
          'w garnizonie;',
          'w Konflikcie;',
          'na planszy jako kontrola, jeżeli efekt to przewiduje.',
        ],
      },
      {
        type: 'heading',
        text: 'Jak przechodzą między strefami?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Zdobycie oddziału zwykle umieszcza go w garnizonie. Rozmieszczenie przenosi go z garnizonu do Konfliktu. Gdy wchodzisz Agentem na pole z ikoną Konfliktu, możesz rozmieścić dozwoloną liczbę oddziałów zgodnie z regułą pola lub akcji. Oddziały w garnizonie nie liczą się do siły Walki.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Marek zdobywa dwa oddziały — trafiają do garnizonu. Później wchodzi Agentem na pole Konfliktu, które pozwala rozmieścić do dwóch oddziałów: przenosi oba z garnizonu do Konfliktu. W garnizonie nie ma już nic. W Fazie Walki liczy te dwa oddziały plus Miecze z Odkrycia. Basia ma trzy oddziały w garnizonie, ale w tej rundzie nie rozmieściła żadnego — jej garnizon nie dodaje siły.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: liczenie garnizonu do Walki; mylenie zdobycia oddziału z automatycznym rozmieszczeniem w Konflikcie.',
      },
      {
        type: 'tip',
        text: 'Po Walce oddziały z Konfliktu wracają zwykle do zasobów, chyba że efekt mówi inaczej. Oddział zdobyty podczas Walki dołącza do trwającego Konfliktu tylko wtedy, gdy efekt wyraźnie każe go rozmieścić. W dodatku Nieśmiertelność oddział użyty jako okaz nie jest dostępny w garnizonie ani Konflikcie.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Garnizon = gotowość; Konflikt = udział w Walce. P: Czy zdobycie oddziału od razu zwiększa siłę? O: Nie, dopóki nie rozmieszczisz go w Konflikcie (albo efekt nie każe inaczej).',
      },
    ],
  },
  {
    title: 'Faza Walki',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Walka rozlicza bieżący Konflikt: liczycie siłę, zagrywacie Intrygi Walki, przyznajecie nagrody i sprzątacie oddziały. To główne źródło skoków punktowych w rundzie.',
      },
      {
        type: 'heading',
        text: 'Jak obliczasz siłę?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Każdy uczestnik sumuje: oddziały w Konflikcie, Miecze z Odkrycia oraz efekty Lidera, kart i Intryg. Nie doliczaj oddziałów pozostających w garnizonie.',
      },
      {
        type: 'heading',
        text: 'Jak przebiega faza?',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Policz wstępną siłę każdego uczestnika (oddziały w Konflikcie + Miecze + jawne efekty).',
          'Gracze mogą zagrywać karty Intrygi Walki w kolejności inicjatywy — stosuj okna timingowe z kart.',
          'Ustal ostateczną siłę.',
          'Przyznaj nagrody za pierwsze, drugie i trzecie miejsce zgodnie z kartą Konfliktu i liczbą graczy.',
          'Rozstrzygnij remisy według reguły Konfliktu: remisujący gracze otrzymują niższe dostępne nagrody zgodnie z oficjalną procedurą.',
          'Zwróć oddziały z Konfliktu do zasobów, chyba że efekt mówi inaczej.',
          'Umieść znacznik kontroli, jeśli nagroda go przyznaje.',
        ],
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Anna ma 3 oddziały w Konflikcie i 2 Miecze z Odkrycia — siła 5 przed Intrygami. Marek ma 2 oddziały i 4 Miecze — siła 6. Basia nie rozmieściła oddziałów i ma 1 Miecz. W kolejności inicjatywy Anna zagrywa Intrygę Walki podaną na karcie; po oknie timingowym porównujecie ostateczne wyniki, przyznajecie nagrody z karty Konfliktu, a oddziały z Konfliktu wracają do zasobów. Garnizon Basi nadal nie daje jej miejsca w nagrodach za siłę, której nie ma w Konflikcie.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: doliczać garnizon; pomijać Intrygi Walki; przyznawać wszystkim remisującym nagrodę za pierwsze miejsce zamiast niższych dostępnych nagród.',
      },
      {
        type: 'tip',
        text: 'Blocker: dokładne tabele nagród i remisów odczytuj z karty Konfliktu / oficjalnej procedury — nie wynajduj wartości. Powiązanie: Intrygi w „Intrygach”; kontrola planszy wraca przy efektach nagród.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Siła = Konflikt + Miecze + efekty; potem Intrygi, nagrody, sprzątanie. P: Czy Miecze są zasobem na później? O: Nie — dotyczą bieżącego Konfliktu. P: Czy oddział zdobyty w Walce wchodzi do niej od razu? O: Tylko gdy efekt każe rozmieścić.',
      },
    ],
  },
  {
    title: 'Frakcje i wpływ',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Cztery tory frakcji dają punkty progowe i Sojusz. Ta część uczy, kiedy dostajesz punkt, kiedy bierzesz Sojusz i dlaczego samo zrównanie wpływu zwykle nie wystarcza do przejęcia tokenu.',
      },
      {
        type: 'heading',
        text: 'Cztery frakcje',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Imperator;',
          'Gildia Kosmiczna;',
          'Bene Gesserit;',
          'Fremeni.',
        ],
      },
      {
        type: 'heading',
        text: 'Jak rośnie wpływ i Sojusz?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Wejście na pola frakcji lub efekty kart podnoszą wpływ. Po osiągnięciu wymaganego progu otrzymujesz punkt frakcji. Pierwszy gracz na wysokim progu zdobywa Sojusz. Jeżeli inny gracz później ma więcej wpływu, przejmuje token Sojuszu i punkt związany z tokenem. Samo zrównanie się z aktualnym liderem zwykle nie wystarcza do przejęcia Sojuszu.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Anna pierwsza osiąga wysoki próg Fremenów i bierze Sojusz oraz związany z nim punkt. Marek później dogania jej wpływ dokładnie do remisu — Sojusz zostaje u Anny. Dopiero gdy Basia wypchnie wpływ powyżej Anny, przejmuje token Sojuszu wraz z punktem tokenu.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: przejmowanie Sojuszu przy remisie wpływu.',
      },
      {
        type: 'tip',
        text: 'Dokładne progi odczytuj z torów na planszy. Powiązanie z polami Agenta i efektami kart w turze.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Próg → punkt; wysoki próg → Sojusz; przejęcie wymaga ściśle więcej wpływu. P: Czy remis zabiera Sojusz? O: Zwykle nie.',
      },
    ],
  },
  {
    title: 'Intrygi',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Intrygi to tajne karty o trzech oknach czasowych. Ta część mówi, kiedy wolno je zagrać i że nie ujawnia się ich wcześniej.',
      },
      {
        type: 'heading',
        text: 'Rodzaje',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Fabuła — zagrywana w podanym momencie tury;',
          'Walka — podczas Fazy Walki;',
          'Koniec gry — po sygnale końca, przed ustaleniem zwycięzcy.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Karty Intrygi są tajne. Nie ujawniaj ich, dopóki ich nie zagrywasz. W Fazie Walki stosuj okna timingowe i kolejność inicjatywy — nie wymyślaj momentu „po wszystkim, na wszelki wypadek”.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Basia trzyma Intrygę Walki zakrytą przez całą rundę. Dopiero w Fazie Walki, w kolejności inicjatywy i we wskazanym oknie karty, ją zagrywa. Osobno Marek ma Intrygę końca gry — nie wolno mu jej użyć w Walce; zachowuje ją na procedurę po sygnale końca.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: pokazywanie Intryg z wyprzedzeniem albo granie karty Walki poza jej oknem timingowym.',
      },
      {
        type: 'tip',
        text: 'Blocker: pełny katalog tekstów Intryg wymaga osobnej weryfikacji. Procedura końca gry jest w rozdziale „Koniec gry”.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Fabuła / Walka / Koniec gry — trzy okna, tajność do zagrania. P: Czy Intrygę Walki wolno zagrać po ustaleniu wszystkich sił „dla pewności”? O: Tylko jeśli okno timingowe i inicjatywa na to pozwalają.',
      },
    ],
  },
  {
    title: 'Wytwórcy',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Faza Wytwórców nagradza ignorowaną pustynię: bonusowa przyprawa kumuluje się na nieodwiedzonych polach i czeka na kogoś, kto wreszcie tam wejdzie.',
      },
      {
        type: 'heading',
        text: 'Jak działa faza?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'W Fazie Wytwórców dodaj Przyprawę na nieodwiedzone pola pustyni z symbolem bonusowej Przyprawy. Jeśli pole nie zostało odwiedzone w kolejnych rundach, bonus kumuluje się. Przyprawa na polu trafia do gracza dopiero po wykonaniu odpowiedniej akcji — samo leżenie na polu nic nie daje właścicielowi „z powietrza”.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Przez dwie rundy nikt nie wchodzi na oznaczone pole pustyni. Po drugiej Fazie Wytwórców leżą tam już skumulowane żetony bonusowej przyprawy. W trzeciej rundzie Anna wchodzi Agentem na to pole odpowiednią akcją i dopiero wtedy zbiera przyprawę z pola.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: zabierać bonusową przyprawę bez akcji wejścia albo zapominać kumulować bonus na kolejnych nieodwiedzonych rundach.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Nieodwiedzone pole → kumulacja; przyprawa do gracza dopiero po akcji. P: Czy bonus znika sam? O: Nie — kumuluje się, aż ktoś odwiedzi pole.',
      },
    ],
  },
  {
    title: 'Powrót',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Powrót zamyka rundę: Agenci wracają, karty idą na odrzucone, inicjatywa się przesuwa i sprawdzacie koniec gry.',
      },
      {
        type: 'heading',
        text: 'Jak przebiega?',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Zwróć Agentów.',
          'Odrzuć zagrane i odkryte karty.',
          'Usuń znaczniki rundy.',
          'Przekaż znacznik pierwszego gracza.',
          'Sprawdź koniec gry.',
        ],
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Po Walce i Wytwórcach wszyscy zwracają Agentów na swoje miejsca, zagrane oraz odkryte karty idą na stosy odrzuconych, schodzą znaczniki rundy, a znacznik pierwszego gracza przechodzi dalej. Dopiero teraz sprawdzacie, czy ktoś ma co najmniej 10 VP albo czy talia Konfliktów jest wyczerpana.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: sprawdzać koniec gry przed sprzątnięciem rundy albo zostawiać Agentów na planszy „na następną rundę”.',
      },
      {
        type: 'tip',
        text: 'Jeśli warunek końca jest spełniony, przejdź do rozdziału „Koniec gry”.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Sprzątanie → inicjatywa → sygnał końca. P: Czy Perswazja zostaje po Powrocie? O: Nie — niewydana Perswazja przepadła już w Odkryciu.',
      },
    ],
  },
  {
    title: 'Rada i Mistrz Miecza',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Dwa stałe cele na planszy zmieniają ekonomię tur: Radny wzmacnia Odkrycie, Mistrz Miecza daje dodatkowego Agenta w przyszłych rundach.',
      },
      {
        type: 'heading',
        text: 'Co dają?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Radny Wysokiej Rady zwiększa Perswazję podczas Odkrycia. Mistrz Miecza daje dodatkowego Agenta na przyszłe rundy. Koszty i pola muszą być wpisane zgodnie z planszą polskiego wydania — odczytaj je z komponentu, zamiast zgadywać.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Anna opłaca koszt wejścia na pole Rady (według planszy), zdobywa Radnego i w kolejnym Odkryciu dolicza bonus Perswazji z Radnego do sumy z kart. Marek w innej rundzie zdobywa Mistrza Miecza — od następnych rund dysponuje dodatkowym Agentem przy wysyłaniu.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: stosować bonus Radnego poza Odkryciem albo traktować Mistrza Miecza jak natychmiastowego drugiego Agenta w tej samej chwili bez sprawdzenia reguły pola.',
      },
      {
        type: 'tip',
        text: 'Blocker: dokładne koszty Solari / warunki pól — z planszy PL. Nie uzupełniono tu liczb bez źródła katalogowego.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Radny → Perswazja w Odkryciu; Mistrz → dodatkowy Agent później. P: Skąd wziąć koszt? O: Z planszy polskiego wydania.',
      },
    ],
  },
  {
    title: 'Nieśmiertelność — przygotowanie',
    requiredAddons: ['immortality'],
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Dodatek Nieśmiertelność dokłada genetykę Bene Tleilax: badania, okazy, osobne karty i Szczepienie. Ta sekcja mówi, co włożyć do gry, w jakiej kolejności to łączyć z podstawą i czego nie zgadywać przy zmianie talii. Po konsolidacji menu cały dodatek jest jednym rozdziałem z wewnętrznymi nagłówkami.',
      },
      {
        type: 'heading',
        text: 'Co wprowadza dodatek?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Do stołu dochodzą elementy, których nie ma w samej podstawie. Każdy z nich ma własną procedurę w dalszych sekcjach tego rozdziału:',
      },
      {
        type: 'step-list',
        steps: [
          'planszę badawczą Tleilaxu;',
          'tor badań;',
          'tor Tleilaxu;',
          'okazy;',
          'karty Tleilaxu;',
          'mechanikę Szczepienia (Graft);',
          'nowe karty Imperium, Intrygi i Liderów.',
        ],
      },
      {
        type: 'heading',
        text: 'Jak przygotować dodatek?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Najpierw rozłóż podstawę jak w rozdziale „Przygotowanie”. Potem dołóż planszę badawczą, znaczniki torów, okazy oraz rząd kart Tleilaxu. Zastosuj nakładki i zmiany talii zgodnie z oficjalnym setupem wydania. [DO WERYFIKACJI: dokładne karty usuwane/dodawane — lista z oficjalnego setupu / komponentów, bez zgadywania]. First Game zaleca dołączyć dodatek dopiero po jednej pełnej partii podstawy.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Po ukończonej partii podstawy Anna, Marek i Basia włączają Nieśmiertelność. Dokładają planszę Tleilaxu obok głównej planszy, ustawiają znaczniki na polach startowych torów (według setupu komponentu) i odkrywają rząd kart Tleilaxu. Nie usuwają ani nie dokładają kart Imperium „na oko” — czekają na oficjalną listę wymiany z pudełka lub zweryfikowanego źródła.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: mieszać komponenty dodatku z podstawą bez oficjalnej listy wymiany kart albo uczyć Szczepienia przed opanowaniem Agenta i Odkrycia.',
      },
      {
        type: 'tip',
        text: 'Dalej w tym rozdziale: Okazy → Tor badań → Tor Tleilaxu → Karty Tleilaxu → Szczepienie. Solo / Ród Hagal pozostaje osobnym trybem przygotowania z podstawy.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Najpierw podstawa, potem Tleilax; listy wymiany kart tylko ze źródła. P: Czy grać dodatek w First Game? O: Nie — po jednej pełnej partii podstawy.',
      },
    ],
  },
  {
    title: 'Okazy',
    requiredAddons: ['immortality'],
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Okazy to osobny zasób badań. Tu uczysz się, skąd się biorą, czym różnią się od garnizonu i Konfliktu oraz dokąd wracają po wydaniu.',
      },
      {
        type: 'heading',
        text: 'Co to jest?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Okazy są specjalnym zasobem reprezentowanym zwykle przez oddziały przeniesione do obszaru badawczego. Oddział użyty jako okaz nie jest dostępny w garnizonie ani Konflikcie — przestaje być pionkiem bojowym, dopóki efekt nie każe inaczej.',
      },
      {
        type: 'heading',
        text: 'Po co istnieją?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Są kosztem badań i zakupów systemu Tleilaxu. Dzięki temu inwestowanie w dodatek boli bojowo: im więcej oddziałów zamienisz w okazy, tym mniej siły masz pod ręką do Konfliktu.',
      },
      {
        type: 'heading',
        text: 'Kiedy ich używasz?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Gdy efekt, pole badawcze albo koszt karty Tleilaxu wymaga okazów. Po wydaniu okaz trafia do miejsca wskazanego przez efekt — zwykle do zasobów ogólnych, nie automatycznie do garnizonu.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Marek przenosi oddział z garnizonu jako okaz do obszaru badawczego. Ten oddział nie wejdzie już do Konfliktu w tej postaci. Później wydaje okaz na efekt Tleilaxu; według tekstu efektu okaz wraca do zasobów ogólnych. Dopiero osobny efekt zdobycia oddziału mógłby znów umieścić go w garnizonie.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: traktowanie okazów jak oddziałów w garnizonie albo zakładanie, że wydany okaz wraca do garnizonu bez instrukcji efektu.',
      },
      {
        type: 'tip',
        text: 'Powiązanie: strefy oddziałów w „Oddziały, garnizon i Konflikt”; zakupy za okazy w „Karty Tleilaxu”.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Okaz ≠ garnizon ≠ Konflikt. P: Czy okaz wraca do garnizonu? O: Nie automatycznie; wykonaj instrukcję efektu, który go wydaje.',
      },
    ],
  },
  {
    title: 'Tor badań',
    requiredAddons: ['immortality'],
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Tor badań to mapa postępu genetycznego na planszy Tleilaxu. Tu uczysz się, kiedy wolno ruszyć znacznik, jak czytać odnogi i skąd brać nagrody pól — bez zgadywania ikon nadrukowanych na komponencie.',
      },
      {
        type: 'heading',
        text: 'Co to jest?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Tor badań to ścieżka (często z rozgałęzieniami), po której przesuwasz znacznik, gdy efekt gry wyraźnie na to pozwala. Każde legalne pole może dawać natychmiastową nagrodę, odblokowywać zdolność albo prowadzić do korzyści związanych z genetyką i kartami.',
      },
      {
        type: 'heading',
        text: 'Po co istnieje?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Tor zamienia inwestycję w badania i okazy na trwały postęp. Wybór odnogi tworzy decyzję strategiczną: jedna ścieżka może faworyzować inne nagrody niż druga. To nie jest „darmowy tor punktów” — ruch kosztuje efekty i okazje, które mógłbyś wydać inaczej.',
      },
      {
        type: 'heading',
        text: 'Kiedy się ruszasz?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Tylko wtedy, gdy efekt karty, pola badawczego albo innej reguły dodatku każe przesunąć znacznik badań. Nie przesuwaj go na początku każdej rundy ani przy zwykłym wejściu Agenta na pole podstawy, chyba że tekst to przewiduje.',
      },
      {
        type: 'heading',
        text: 'Jak przebiega ruch?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Sprawdź legalne przejścia na planszy badawczej. Przesuń znacznik na dozwolone kolejne pole. Natychmiast weź nagrodę nadrukowaną na tym polu — odczytaj ją z komponentu. Jeśli tor rozgałęzia się, wybór odnogi może być trwały; nie wracaj „bo zmieniłeś zdanie”, gdy plansza oznacza wybór jako stały.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Anna otrzymuje efekt pozwalający przesunąć znacznik badań o jedno pole. Na planszy ma dwie legalne odnogi. Wybiera lewą, przesuwa znacznik i bierze nagrodę nadrukowaną na tym polu. W kolejnej turze nie może „przeskoczyć” na prawą odnogę z pominięciem reguł przejścia tylko dlatego, że wygląda korzystniej.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: traktować wybór odnogi jako tymczasowy, gdy tor oznacza go jako trwały; zgadywać nagrody pól zamiast czytać planszę.',
      },
      {
        type: 'tip',
        text: '[DO WERYFIKACJI] Katalog nagród pól toru badań — wyłącznie z planszy / zweryfikowanego źródła. Powiązanie: okazy i tor Tleilaxu w tym samym rozdziale Nieśmiertelność.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Ruch tylko z efektu; nagroda z pola; odnoga może być trwała. P: Czy mogę ruszyć tor badań bez efektu? O: Nie. P: Skąd wziąć nagrodę pola? O: Z nadruku na planszy badawczej.',
      },
    ],
  },
  {
    title: 'Tor Tleilaxu',
    requiredAddons: ['immortality'],
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Tor Tleilaxu mierzy relację z Bene Tleilax osobno od toru badań. Tu uczysz się, kiedy znacznik się przesuwa i co dają progi — bez mylenia go z torami frakcji podstawy.',
      },
      {
        type: 'heading',
        text: 'Co to jest?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Tor Tleilaxu odzwierciedla postęp w relacji z Bene Tleilax. Progi dają nagrody, punkty albo dostęp do kart. To osobny tor od badań i od czterech frakcji podstawy (Imperator, Gildia, Bene Gesserit, Fremeni).',
      },
      {
        type: 'heading',
        text: 'Po co istnieje?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Daje długoterminową nagrodę za inwestowanie w system dodatku. Progi mogą odblokować mocniejsze zakupy Tleilaxu albo punkty — dlatego warto planować efekty przesuwające ten tor, a nie traktować go jak ozdoby.',
      },
      {
        type: 'heading',
        text: 'Kiedy się przesuwa?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Znacznik przesuwa się tylko przez wyraźne efekty (karty Tleilaxu, pola, Liderzy dodatku itd.). Nie przesuwaj go automatycznie przy każdym wejściu na pole planszy podstawy ani przy każdym zdobyciu okazu, chyba że tekst tak każe.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Efekt karty Tleilaxu każe Basi przesunąć znacznik o jedno pole. Basia przesuwa go i sprawdza, czy właśnie osiągnęła próg. Dopiero po osiągnięciu progu otrzymuje wskazaną nagrodę progu. W tej samej rundzie wchodzi Agentem na pole Fremenów — wpływ Fremenów rośnie, ale tor Tleilaxu stoi w miejscu, bo żadne wejście na pole podstawy go nie ruszyło.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: przesuwanie toru Tleilaxu przy każdym wejściu na dowolne pole planszy podstawy albo mylenie go z torem badań.',
      },
      {
        type: 'tip',
        text: 'Powiązanie: progi mogą wpływać na legalność zakupu kart Tleilaxu. Frakcje podstawy opisuje rozdział „Frakcje i wpływ”.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Tylko wyraźne efekty; progi dają nagrody. P: Czy tor Tleilaxu = tor Bene Gesserit? O: Nie — to osobny system dodatku.',
      },
    ],
  },
  {
    title: 'Karty Tleilaxu',
    requiredAddons: ['immortality'],
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Karty Tleilaxu to osobny rynek zakupu dodatku. Tu uczysz się, że koszt może być inny niż w rzędzie Imperium i że teksty konkretnych kart odczytujesz z komponentów.',
      },
      {
        type: 'heading',
        text: 'Co to jest?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Karty Tleilaxu tworzą oddzielny rząd (wystawkę) zakupu. Ich zakup może wymagać okazów, Perswazji, pozycji na torze albo kombinacji tych wymagań — zawsze według tekstu karty i reguł dodatku, nie według domysłu z rzędu Imperium.',
      },
      {
        type: 'heading',
        text: 'Po co istnieje osobny rząd?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Oddziela genetykę Bene Tleilax od zwykłego rynku Imperium. Dzięki temu decyzja o okazach i torach ma sens zakupowy: inwestujesz w system dodatku, zamiast kupować „to samo co zawsze” za samą Perswazję.',
      },
      {
        type: 'heading',
        text: 'Kiedy kupujesz?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Zwykle w oknie zakupów podczas tury Odkrycia — o ile spełniasz wymagania karty. Po zakupie uzupełnij wystawkę według oficjalnej reguły uzupełniania rzędu Tleilaxu.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Marek w Odkryciu ma Perswazję i dwa okazy. Karta Tleilaxu w rzędzie wymaga okazów (oraz ewentualnie innego warunku — odczytaj z karty). Marek płaci wymagany koszt, kupuje kartę do strefy wskazanej przez regułę zakupu i uzupełnia lukę w rzędzie Tleilaxu. Nie kupuje jej „za samą Perswazję jak Imperium”, jeśli karta wymaga okazów.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: kupować karty Tleilaxu wyłącznie za Perswazję, gdy karta wymaga okazów lub progu; mylić rząd Tleilaxu z rzędem Imperium.',
      },
      {
        type: 'tip',
        text: '[DO WERYFIKACJI] Pełny katalog kart Tleilaxu — osobna weryfikacja komponentów; tu tylko procedura zakupu i uzupełniania.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Osobny rząd; koszt z karty; uzupełnij wystawkę. P: Czy zawsze płacę tylko Perswazją? O: Nie — czytaj wymagania karty (okazy, progi, inne).',
      },
    ],
  },
  {
    title: 'Szczepienie',
    requiredAddons: ['immortality'],
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Szczepienie (Graft) to kluczowa nowość Nieśmiertelności przy zagrywaniu kart. Ta część uczy, jak legalnie połączyć dwie karty i czego ta zagrywka nigdy nie robi automatycznie.',
      },
      {
        type: 'heading',
        text: 'Co to jest?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Szczepienie pozwala połączyć dwie kompatybilne karty i użyć ich wspólnie w jednej turze — jako jedną zagrywkę Agenta albo jedną zagrywkę Odkrycia, zgodnie z ikoną lub zdolnością Szczepienia oraz regułami kompatybilności na kartach.',
      },
      {
        type: 'heading',
        text: 'Po co istnieje?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Zwiększa elastyczność talii dodatku: możesz łączyć ikony, Perswazję albo efekty, których osobno nie zagrasz w tym samym oknie. To nagroda za inwestowanie w system Tleilaxu, nie skrót do podwójnego tempa Agentów.',
      },
      {
        type: 'heading',
        text: 'Kiedy wolno szczepić?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Gdy masz kartę z ikoną lub zdolnością Szczepienia oraz legalną drugą kartę spełniającą warunki połączenia. Kompatybilność i kolejność efektów odczytujesz z tekstów kart — bez zgadywania katalogu Graft.',
      },
      {
        type: 'heading',
        text: 'Jak przebiega?',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Wybierz kartę z ikoną lub zdolnością Szczepienia.',
          'Połącz ją z legalną drugą kartą.',
          'Traktuj zestaw jako jedną zagrywkę zgodnie z zasadami Agenta lub Odkrycia.',
          'Rozpatrz efekty w dozwolonej kolejności.',
          'Obie karty trafiają później do właściwej strefy.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Nie oznacza to automatycznie dwóch Agentów ani wejścia na dwa pola.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Anna ma kartę ze Szczepieniem i legalną drugą kartę. Łączy je w jedną zagrywkę Agenta: używa ikon i efektów zgodnie z regułą połączenia, stawia jednego Agenta na jednym polu i rozpatruje efekty w dozwolonej kolejności. Nie stawia drugiego Agenta „bo były dwie karty”. W innym przypadku, gdy szczepi w trybie Odkrycia, obie karty liczą się do jednej sumy Perswazji i Mieczy — nadal jako jedna decyzja Odkrycia, nie jako dodatkowa tura Agenta.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: wysyłanie dwóch Agentów przez Szczepienie; wchodzenie na dwa pola „bo były dwie karty”; łączenie kart bez spełnienia warunków kompatybilności.',
      },
      {
        type: 'tip',
        text: 'Wyjątek / blocker: dokładne ikony Graft i listy kompatybilności — z komponentów kart Tleilaxu / Imperium dodatku. Powiązanie: jedna karta = jeden tryb w „Anatomii karty”; tura Agenta i Odkrycia bez dodatku najpierw w First Game.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Dwie kompatybilne karty → jedna zagrywka. Szczepienie nie mnoży Agentów ani pól samo z siebie.',
      },
      {
        type: 'heading',
        text: 'Mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'P: Czy Szczepienie = dwa pola? O: Nie. P: Czy obie karty są zużyte w tej zagrywce? O: Tak — trafiają potem do właściwej strefy zgodnie z trybem (Agent/Odkrycie). P: Czy mogę doszczepić trzecią kartę „przy okazji”? O: Tylko jeśli tekst zdolności na to pozwala — nie domyślaj.',
      },
    ],
  },
  {
    title: 'Koniec gry',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Po sygnale końca nie porównujecie od razu torów punktacji. Najpierw Intrygi końca gry i efekty kart oraz Liderów, potem wynik i remisy.',
      },
      {
        type: 'heading',
        text: 'Jak przebiega procedura?',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Zagraj karty Intrygi końca gry.',
          'Rozpatrz efekty kart i Liderów.',
          'Dodaj lub odejmij punkty.',
          'Ustal największy wynik.',
          'Zastosuj oficjalne kryteria remisu.',
        ],
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Po Powrocie Anna ma 10 VP, Marek 9, Basia 8. Gracze zagrywają Intrygi końca gry: Marek zyskuje 2 punkty i wychodzi na 11. Dopiero wtedy porównujecie wyniki. Gdyby padł remis, stosujecie oficjalne kryteria remisu — bez wymyślania własnej kolejności „kto miał więcej przyprawy”.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: ogłaszać zwycięzcę przed Intrygami końca gry.',
      },
      {
        type: 'tip',
        text: 'Blocker: oficjalna tabela remisów / dane z komponentów — DO WERYFIKACJI, jeśli nie masz źródła przy stole.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Intrygi końca → efekty → porównanie → remis oficjalny. P: Czy 10 VP kończy natychmiast w turze? O: Nie — po zakończeniu rundy i tej procedurze.',
      },
    ],
  },
  {
    title: 'Pełny przykład rundy',
    content: [
      {
        type: 'heading',
        text: 'Po co ten przykład?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Poniższy przebieg łączy Agentów, Odkrycie, Walkę, Wytwórców i Powrót w jedną historię przy stole — bez wymyślania tekstów konkretnych kart. Czytaj go po rozdziałach faz; potem zagrajcie First Game.',
      },
      {
        type: 'heading',
        text: 'Przebieg przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Na początku rundy odkrywacie nową kartę Konfliktu. Anna zagrywa kartę z ikoną Bene Gesserit, wysyła Agenta na wolne pole frakcji, opłaca koszt wejścia, otrzymuje wpływ i rozpatruje górny efekt karty. W kolejnej własnej turze wchodzi na wolne pole z ikoną Konfliktu i rozmieszcza dwa oddziały z garnizonu do Konfliktu.',
      },
      {
        type: 'paragraph',
        text: 'Gdy Anna nie chce już wysyłać Agentów, wykonuje Odkrycie: odkrywa pozostałe karty, sumuje Perswazję i Miecze, kupuje jedną kartę z rzędu Imperium za Perswazję (na stos odrzuconych), a niewydana Perswazja przepada. Miecze zapisuje do bieżącej Walki.',
      },
      {
        type: 'paragraph',
        text: 'Gdy wszyscy skończą tury, następuje Walka: Anna liczy oddziały w Konflikcie plus Miecze plus jawne efekty; Intrygi Walki — w kolejności inicjatywy. Dostaje nagrodę miejsca zgodnie z kartą Konfliktu. Potem Faza Wytwórców dokłada bonusową Przyprawę na nieodwiedzone pola pustyni z symbolem. W Powrocie Agenci wracają, karty idą na odrzucone, przekazywany jest znacznik pierwszego gracza i sprawdzacie warunek końca gry.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd w przykładzie treningowym: pominąć Powrót albo ogłosić zwycięzcę w chwili zdobycia 10 VP bez Intryg końca gry.',
      },
      {
        type: 'tip',
        text: 'Ćwicz ten rytm w First Game bez dodatku; Nieśmiertelność dołącz po jednej pełnej partii.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Konflikt → Agent/Odkrycie → Walka → Wytwórcy → Powrót. P: Czy ten przykład zastępuje teksty kart? O: Nie — karty i nagrody Konfliktów czytasz z komponentów.',
      },
    ],
  },
  {
    title: 'FIRST GAME',
    content: [
      {
        type: 'paragraph',
        text: 'Pierwsza partia ma nauczyć różnicy Agent vs Odkrycie oraz garnizon vs Konflikt. Trzymaj się tej listy, zanim włączysz Nieśmiertelność.',
      },
      {
        type: 'step-list',
        steps: [
          'Graj 3–4 osobowo bez dodatku.',
          'Wyjaśnij różnicę między efektem Agenta i Odkrycia.',
          'Pokaż garnizon oraz Konflikt osobno.',
          'Nie trzymaj Perswazji między rundami.',
          'Przed pierwszą Walką policz siłę każdego gracza.',
          'Dodatek Nieśmiertelność dołącz po jednej pełnej partii.',
        ],
      },
    ],
  },
  {
    title: 'QUICK REFERENCE',
    content: [
      {
        type: 'paragraph',
        text: 'Runda: Konflikt → tury Agenta/Odkrycia → Walka → Wytwórcy → Powrót.',
      },
      {
        type: 'paragraph',
        text: 'Agent: karta + ikona + wolne pole + koszt + efekt pola + efekt karty.',
      },
      {
        type: 'paragraph',
        text: 'Odkrycie: Perswazja na zakup, Miecze do Walki; niewydana Perswazja przepada; zakup zwykle na odrzucone.',
      },
      {
        type: 'paragraph',
        text: 'Siła: oddziały w Konflikcie + Miecze + efekty (nie garnizon).',
      },
      {
        type: 'paragraph',
        text: 'Koniec: 10 VP lub brak Konfliktów — po rundzie; potem Intrygi końca gry.',
      },
      {
        type: 'paragraph',
        text: 'Sojusz: przejęcie przy ściśle wyższym wpływie, nie przy remisie.',
      },
      {
        type: 'paragraph',
        text: 'Nieśmiertelność: okazy ≠ garnizon; Szczepienie ≠ dwóch Agentów.',
      },
    ],
  },
  {
    title: 'NAJCZĘSTSZE BŁĘDY',
    content: [
      {
        type: 'step-list',
        steps: [
          'Używanie tej samej karty jako Agenta i w Odkryciu.',
          'Liczenie garnizonu do Walki.',
          'Kupowanie kart za niewydane zasoby zamiast Perswazji.',
          'Przenoszenie nowej karty prosto do ręki.',
          'Przejmowanie Sojuszu przy remisie wpływu.',
          'Wysyłanie dwóch Agentów przez Szczepienie.',
          'Traktowanie okazów jak oddziałów w garnizonie.',
          'Ogłaszanie zwycięzcy przed Intrygami końca gry.',
        ],
      },
    ],
  },
  {
    title: 'FAQ',
    content: [
      {
        type: 'paragraph',
        text: 'Czy mogę wykonać Odkrycie, mając jeszcze Agenta? Tak. Po Odkryciu nie wysyłasz już Agentów w tej rundzie.',
      },
      {
        type: 'paragraph',
        text: 'Czy muszę kupić kartę? Nie.',
      },
      {
        type: 'paragraph',
        text: 'Czy niewydana Perswazja przechodzi? Nie.',
      },
      {
        type: 'paragraph',
        text: 'Czy Miecze są zasobem? Nie, to siła bieżącego Konfliktu.',
      },
      {
        type: 'paragraph',
        text: 'Czy oddział zdobyty podczas Walki dołącza do trwającego Konfliktu? Tylko jeśli efekt wyraźnie mówi o rozmieszczeniu.',
      },
      {
        type: 'paragraph',
        text: 'Czy karta Walki może być zagrana po ujawnieniu wszystkich sił? Stosuj okna timingowe i kolejność inicjatywy.',
      },
      {
        type: 'paragraph',
        text: 'Czy okaz wraca do garnizonu? Nie automatycznie; wykonaj instrukcję efektu, który go wydaje.',
      },
    ],
  },
  {
    title: 'GLOSSARY',
    content: [
      {
        type: 'step-list',
        steps: [
          'Agent — pion wysyłany na pole planszy.',
          'Odkrycie — użycie dolnych części pozostałych kart.',
          'Perswazja — siła zakupu kart.',
          'Miecz — siła w bieżącym Konflikcie.',
          'Garnizon — oddziały gotowe do rozmieszczenia.',
          'Konflikt — obszar oddziałów uczestniczących w Walce.',
          'Sojusz — token za najwyższy wpływ we frakcji.',
          'Okaz — zasób dodatku Nieśmiertelność.',
          'Szczepienie — połączenie dwóch kart w jedną zagrywkę.',
        ],
      },
    ],
  },
];

export const diunaImperiumFirstGame: FirstGameStep[] = [
  {
    id: 1,
    title: 'Zagrajcie bez dodatku',
    content: [
      'Ustawcie partię 3–4 osobową bez Nieśmiertelności.',
      'Dokończcie pełne przygotowanie z rozdziału „Przygotowanie”.',
    ],
    why: 'Mniej systemów naraz: najpierw Agent, Odkrycie i Konflikt.',
    commonMistake: 'Włączanie Tleilaxu / Szczepienia w pierwszej partii.',
    chapterIndex: 2,
  },
  {
    id: 2,
    title: 'Agent kontra Odkrycie',
    content: [
      'Pokażcie na jednej karcie górny efekt Agenta i dolne Odkrycie.',
      'Podkreślcie: w jednej rundzie karta idzie albo w Agent, albo w Odkrycie.',
    ],
    why: 'To najczęstszy błąd początkujących i fundament każdej tury.',
    commonMistake: 'Doliczanie Perswazji z karty już zagranej Agentem.',
    chapterIndex: 3,
  },
  {
    id: 3,
    title: 'Garnizon i Konflikt osobno',
    content: [
      'Połóżcie oddziały najpierw w garnizonie.',
      'Rozmieśćcie je do Konfliktu dopiero z pola z ikoną Konfliktu.',
    ],
    why: 'Bez tej różnicy Faza Walki liczy się źle.',
    commonMistake: 'Liczenie garnizonu jako siły Walki.',
    chapterIndex: 10,
  },
  {
    id: 4,
    title: 'Perswazja nie przechodzi',
    content: [
      'Przy pierwszym Odkryciu zsumujcie Perswazję, kupcie kartę lub nie, a resztę odrzućcie mentalnie.',
      'Kupioną kartę odłóżcie zwykle na stos odrzuconych.',
    ],
    why: 'Perswazja nie jest zasobem między rundami.',
    commonMistake: 'Trzymanie Perswazji „na później” albo dokładanie zakupu do ręki.',
    chapterIndex: 7,
  },
  {
    id: 5,
    title: 'Policzcie pierwszą Walkę',
    content: [
      'Przed nagrodami policzcie na głos: oddziały w Konflikcie + Miecze + jawne efekty.',
      'Dopiero potem Intrygi Walki w kolejności inicjatywy.',
    ],
    why: 'Uczy okna Walki zanim punkty zaczną skakać.',
    commonMistake: 'Pomijanie Intryg albo doliczanie garnizonu.',
    chapterIndex: 11,
  },
  {
    id: 6,
    title: 'Dodatek po pełnej partii',
    content: [
      'Po jednej ukończonej partii podstawy włączcie Nieśmiertelność.',
      'Przeczytajcie jeden rozdział dodatku: okazy, tory, Tleilaxu, Szczepienie.',
    ],
    why: 'Szczepienie i okazy budują na opanowanym cyklu Agenta/Odkrycia.',
    commonMistake: 'Traktowanie Szczepienia jak dwóch Agentów.',
    chapterIndex: 17,
    requiredAddons: ['immortality'],
  },
];
